import { ReactNode, useRef, useState } from "react";
import { PlacesType, Tooltip } from "react-tooltip";

import { Client } from "@/Common/Api/Client";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { FaxHelper } from "@/Common/Helpers/FaxHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { EnumSentFaxItemType } from "@/Common/Models/Faxing/EnumSentFaxItemType";
import { EnumSentFaxStatus } from "@/Common/Models/Faxing/EnumSentFaxStatus";
import { SentFaxUiStatus } from "@/Common/Models/UI/SentFaxUiStatus";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { IconButton, IconButtonSize } from "@/DesignSystem/Molecules/IconButton/IconButton";
import { SentFaxStatusHelper } from "@/Patient/Helpers/SentFaxStatusHelper";
import { FaxStatusIconButton } from "./FaxStatusIconButton";
import { ResendFaxButton } from "./ResendFaxButton";
import { SentFaxIcon } from "./SentFaxIcon";

import styles from "./SentFaxInfoBox.module.scss";

import PaperClip from "@/DesignSystem/Atoms/Icons/Types/Paperclip/paperclip-regular.svg";

interface ISentFaxData {
    recipient: string;
    recipientFax: string;
    sentFaxItemId: string;
    faxId: number;
    senderEmployeeId: string;
    sentBy: string;
    sentDate: string;
    receivedDate?: string;
    faxStatus: EnumSentFaxStatus;
    itemType: EnumSentFaxItemType;
    hasAttachment?: boolean;
}

interface IReactTooltipComponentProps {
    hasFooter?: boolean;
    maxWidth?: number | string;
    minWidth?: number | string;
    maxHeight?: number | string;
    tooltipContent?: JSX.Element | string;
    footerContent?: JSX.Element;
    parentRect?: DOMRect;
    triggerRect?: DOMRect;
    className?: string;
}

interface ISentFaxInfoBoxProps extends IReactTooltipComponentProps {
    data: ISentFaxData[];
    hidden?: boolean;
    children?: ReactNode;
    minDisplayedItems?: number;
    tooltipId?: string;
    parentElementId?: string;
    iconButtonSize?: IconButtonSize;
    iconBtnWrapperClassName?: string;
    hasBorder?: boolean;
    padding?: number;
    width?: number | string;
    initialPlace?: PlacesType;
    handleTooltipOpen?: (isOpen: boolean) => void;
}

const ITEM_HEIGHT = 250;
const PADDING_TO_PARENT = 15;

function SentFaxInfoBox(props: ISentFaxInfoBoxProps): JSX.Element {
    const { getText } = useTranslation();
    const {
        minDisplayedItems = 2,
        hasBorder = true,
        padding = 16,
        initialPlace = "bottom-end",
        width = 275,
        hidden
    } = props;
    const faxIconRef = useRef<HTMLDivElement>();
    const contentRef = useRef<HTMLDivElement>();
    const footerRef = useRef<HTMLDivElement>();
    const [displayedItemsCount, setDisplayedItemsCount] = useState(minDisplayedItems);
    const [tooltipPlace, setTooltipPlace] = useState<PlacesType>(initialPlace);
    const isExtended = displayedItemsCount > minDisplayedItems;
    const defaultMaxHeight = minDisplayedItems * ITEM_HEIGHT;
    const [maxHeight, setMaxHeight] = useState(defaultMaxHeight);
    const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(false);

    const parentElement = props.parentElementId ? document.querySelector<HTMLElement>(`#${props.parentElementId}`) : null;
    const parentRect = parentElement?.getBoundingClientRect();
    const triggerRect = faxIconRef.current?.getBoundingClientRect();

    function onExpandOrCollapse(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, count: number): void {
        e.preventDefault();
        adjustMaxHeight(tooltipPlace);
        setDisplayedItemsCount(count);
    }

    function afterShow(): void {
        if (contentRef.current) {
            const contentRect = contentRef.current.getBoundingClientRect();

            const spaceBelow = (parentRect ? parentRect.bottom : window.innerHeight) - triggerRect.bottom;
            const spaceAbove = parentRect ? triggerRect.top - parentRect.top : triggerRect.top;
            const spaceRight = (parentRect ? parentRect.right : window.innerWidth) - triggerRect.right;
            const spaceLeft = parentRect ? triggerRect.left - parentRect.left : triggerRect.left;

            const showBelow = spaceBelow > contentRect.height || spaceAbove < spaceBelow;
            const showRight = spaceLeft > contentRect.width || spaceLeft > spaceRight;
            const currentPlace: PlacesType = showBelow
                ? (showRight ? "bottom-end" : "bottom-start")
                : (showRight ? "top-end" : "top-start");

            adjustMaxHeight(currentPlace);
            setTooltipPlace(currentPlace);
        }
    }

    function afterHide(): void {
        setMaxHeight(defaultMaxHeight);
        setDisplayedItemsCount(minDisplayedItems);
    }

    function adjustMaxHeight(currentPlace: PlacesType): void {
        if (contentRef.current) {
            const footerHeight = footerRef.current?.getBoundingClientRect().height ?? 0;

            if (currentPlace === "top-start" || currentPlace === "top-end") {
                const spaceAbove = (parentRect ? triggerRect.top - parentRect.top : triggerRect.top) - PADDING_TO_PARENT;
                const maxExtendedHeight = isExtended ? Math.min(spaceAbove - footerHeight, defaultMaxHeight) : spaceAbove - footerHeight;

                setMaxHeight(maxExtendedHeight);
            }
            else {
                const spaceBelow = (parentRect ? parentRect.bottom : window.innerHeight) - triggerRect.bottom - PADDING_TO_PARENT;
                const maxExtendedHeight = isExtended ? Math.min(spaceBelow - footerHeight, defaultMaxHeight) : spaceBelow - footerHeight;

                setMaxHeight(maxExtendedHeight);
            }
        }
    }

    async function handleAttachmentClick(sentFax: ISentFaxData): Promise<void> {
        const withSignature = FaxHelper.isPinRequired(sentFax.itemType) &&
            sentFax.senderEmployeeId == sessionStore.getCurrentEmployee().ContactFacetId;

        const response = await Client.postAsync<{ FaxPreviewURL: string, IsPermanentFaxMessageFilePath: boolean }>(
            ServerOperations.Server.Portfolio_GetSentFaxPreview,
            {
                SentFaxItemId: sentFax.faxId,
                WithSignature: withSignature
            }
        );

        const route = FaxHelper.getPdfPreviewRoute(sentFax.faxId, response.data.FaxPreviewURL, response.data.IsPermanentFaxMessageFilePath, withSignature);
        const url = route ? apiSettings.buildAppUrl(route, { auth: true }) : null;

        MyleHelper.OpenNewWindow(url);
    }

    return (
        <>
            <FaxStatusIconButton
                ref={faxIconRef}
                isClicked={isTooltipOpen}
                tooltipTriggerId={props.tooltipId}
                statuses={props.data.map(fax => fax.faxStatus)}
                iconSize={props.iconButtonSize}
                iconButtonWrapperClasses={props.iconBtnWrapperClassName}
            />
            <Tooltip
                {...props}
                id={props.tooltipId}
                clickable
                afterShow={() => afterShow()}
                afterHide={() => afterHide()}
                openOnClick
                isOpen={isTooltipOpen}
                globalCloseEvents={{
                    clickOutsideAnchor: true,
                    scroll: isTooltipOpen
                }}
                noArrow
                closeEvents={{
                    click: true,
                    dblclick: true
                }}
                opacity={1}
                className={`${styles.tooltip} ${props.className}`}
                place={tooltipPlace}
                setIsOpen={() => setIsTooltipOpen(prev => !prev)}
            >
                <div
                    ref={contentRef}
                    className={`${styles.tooltipContainer} ${!hidden ? styles.pointer : ""}`}
                >
                    {!hidden && (
                        <div
                            className={`${styles.reactTooltipContent} ${hasBorder ? styles.border : ""}`}
                            onClick={e => e.stopPropagation()}
                        >
                            <div
                                className={styles.inner}
                                style={{
                                    padding,
                                    width,
                                    maxWidth: props.maxWidth,
                                    minWidth: props.minWidth,
                                    maxHeight
                                }}
                            >
                                <div className={styles.contentWrapper}>
                                    <p className={styles.recipient}>{getText("SentFaxInfoBox_Recipient")}</p>
                                    {props.data.slice(0, displayedItemsCount).map((faxData, faxIndex, self) => {
                                        const status = SentFaxStatusHelper.convertToUiStatus(faxData.faxStatus);
                                        const hideDivider = self.length < 1 || faxIndex + 1 === self.length;

                                        const dateFormat = `yyyy/LL/dd '${getText("AtLabel")}' HH:mm:ss a`;
                                        const sentDateStr = LuxonDateTimeParser.parse(faxData.sentDate).toFormat(dateFormat);
                                        const receivedDateStr = status === SentFaxUiStatus.Sent && faxData.receivedDate
                                            ? LuxonDateTimeParser.parse(faxData.receivedDate).toFormat(dateFormat)
                                            : "-";
                                        const senderEmployee = cacheStore.GetEmployeeByContactFacetId(faxData.senderEmployeeId);
                                        const senderEmployeePrefix = cacheStore.GetLookupById(senderEmployee.PrefixLookupId)?.GetValue() ?? "";

                                        return (
                                            <div
                                                key={faxData.faxId}
                                                className={`${styles.tooltipContent} ${!hideDivider ? styles.divider : ""}`}
                                            >
                                                <div className={styles.faxSentBy}>
                                                    <p>{getText("SentFaxInfoBox_SentBy")}</p>
                                                    <p className={styles.singleLineEllipsis}>
                                                        {`${senderEmployeePrefix} ${senderEmployee.LastName}, ${senderEmployee.FirstName}`}
                                                    </p>
                                                </div>

                                                <div>
                                                    <div className={styles.recipientContent}>
                                                        <strong className={`${styles.recipientName} ${styles.singleLineEllipsis}`}>{faxData.recipient}</strong>
                                                        <div className={styles.iconWrapper}>
                                                            <SentFaxIcon status={status} />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <p className={styles.faxText}>{getText("FaxDLabel")} {FaxHelper.displayFaxNumberFormat(faxData.recipientFax)}</p>
                                                    </div>
                                                </div>

                                                <div className={styles.datesWrapper}>
                                                    <div className={styles.dateWrapper}>{getText("SentFaxInfoBox_SentDate")}
                                                        <p className={styles.dateText}>{sentDateStr}</p>
                                                    </div>
                                                    <div className={styles.dateWrapper}>{getText("SentFaxInfoBox_ReceivedDate")}
                                                        <p className={styles.dateText}>{receivedDateStr}</p>
                                                    </div>
                                                </div>

                                                {faxData.hasAttachment && (
                                                    <div className={`${styles.singleLineEllipsis} ${styles.attachmentWrapper}`}>
                                                        <IconButton
                                                            size="sm"
                                                            variant="neutral"
                                                            category="status-icon"
                                                            onClick={() => handleAttachmentClick(faxData)}
                                                        >
                                                            <div className={styles.attachmentsIconWrapper}>
                                                                <PaperClip />
                                                                <span className={styles.attachmentsLabel}>{getText("SentFaxInfoBox_OtherAttachmentsLabel")}</span>
                                                            </div>
                                                        </IconButton>
                                                    </div>
                                                )}

                                                {status === SentFaxUiStatus.Failed && (
                                                    <div className={styles.buttonsWrapper} data-cy="send-fax-indicator-resend">
                                                        <ResendFaxButton
                                                            sentFaxItemId={faxData.sentFaxItemId}
                                                            sentFaxIds={[faxData.faxId]}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {props.data.length > minDisplayedItems && (
                                <div
                                    ref={footerRef}
                                    className={styles.footer}
                                    style={{
                                        paddingLeft: padding,
                                        paddingRight: padding
                                    }}
                                >
                                    <a className={styles.viewAction} onClick={e => onExpandOrCollapse(e, isExtended ? minDisplayedItems : props.data.length)}>
                                        <span className="push-right-5">{getText(isExtended ? "SentFaxInfoBox_ViewLess" : "SentFaxInfoBox_ViewMore")}</span>
                                        <span className={`glyphicon ${isExtended ? "glyphicon-chevron-up" : "glyphicon-chevron-down"}`} />
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Tooltip>
        </>
    );
}

export { ISentFaxData, SentFaxInfoBox };
